import { create } from "zustand";
import { audioContext, useVolumes } from "./volume";
import { useStreamer } from "@shared/ui/streamer";

/**
 * Звуковые указания: короткий звук на событие.
 *
 * Пять штук — сообщение, вход и выход из голосового канала, запуск и
 * остановка показа экрана. Файлы собраны заранее (`npm run sounds:build`) и
 * лежат в `public/sounds/` готовыми: обрезанные, выровненные по громкости и
 * со сглаженными краями. Здесь остаётся только сыграть.
 *
 * Отдельный модуль, а не по месту события, ровно по одному поводу: звать
 * будут четверо (переписка, звонок, показ, настройки), и `new Audio(...)` в
 * четырёх местах — это четыре разных ответа на вопросы «а если звук ещё не
 * загрузился», «а если событий десять подряд» и «а какая тут громкость».
 */

/** Что умеем играть. Имя — оно же имя файла в `public/sounds/`. */
export type Cue = "notify" | "voice-join" | "voice-leave" | "share-start" | "share-stop";

/**
 * Громкость указаний — своим ключом в том же хранилище, что и остальные.
 *
 * Ключ свой, а не общий с `MASTER_KEY`, потому что стороны разные: тот
 * ведёт чужой звук — голоса и трансляции, — а указание подаёт само
 * приложение. Убавить собеседников и не потерять оклик о сообщении должно
 * быть можно; через общий узел это одно движение.
 *
 * Отдельного выключателя нет намеренно: ноль на этой шкале и есть «молчать».
 * Довод тот же, по которому его нет у громкости собеседника, — см.
 * `toggleMute` в `volume.ts`: два выключателя на один звук порождают
 * состояние «выключено, но громкость 80 %», которое нечем объяснить.
 */
export const CUE_KEY = "cue";

/** По умолчанию указания тише голосов: они не разговор, а отметка на полях. */
export const CUE_DEFAULT = 0.6;

export function cueVolume(): number {
  return useVolumes.getState()[CUE_KEY] ?? CUE_DEFAULT;
}

/**
 * Какие указания выключены — по событию, как в Discord (Уведомления → Звуки).
 *
 * Это не второй выключатель громкости: громкость решает «насколько слышно»
 * всё сразу, а здесь — «на какие события звать вообще». Не нужен звук входа
 * в канал — не значит, что не нужен звук о сообщении.
 */
const OFF_KEY = "airel.cues-off";

function readOff(): Record<string, true> {
  try {
    const raw = localStorage.getItem(OFF_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? (parsed as Record<string, true>) : {};
  } catch {
    return {};
  }
}

export const useCuesOff = create<Record<string, true>>()(() => readOff());

export function setCueOn(name: Cue, on: boolean) {
  const next = { ...useCuesOff.getState() };
  if (on) delete next[name];
  else next[name] = true;
  useCuesOff.setState(next, true);

  try {
    localStorage.setItem(OFF_KEY, JSON.stringify(next));
  } catch {
    // Не записалось — выбор доживёт до конца сеанса в памяти.
  }
}

/**
 * Разобранные звуки.
 *
 * Хранится обещание, а не готовый буфер: два события подряд на непрогретом
 * звуке дали бы две загрузки одного файла. Промах (нет файла, не разобрался)
 * остаётся в карте отрицательным ответом — сеть на каждый чих дёргать
 * незачем, а второй раз он всё равно не разберётся.
 */
const loaded = new Map<Cue, Promise<AudioBuffer | null>>();

function load(name: Cue): Promise<AudioBuffer | null> {
  const known = loaded.get(name);
  if (known) return known;

  const loading = (async () => {
    try {
      // От `BASE_URL`, а не от корня: в оболочке страница открыта файлом, и
      // «/sounds» указывал бы в корень диска — указаний в ней не было слышно.
      const response = await fetch(`${import.meta.env.BASE_URL}sounds/${name}.wav`);
      if (!response.ok) return null;
      return await audioContext().decodeAudioData(await response.arrayBuffer());
    } catch {
      // Звук — не то, ради чего стоит ронять обработчик события.
      return null;
    }
  })();

  loaded.set(name, loading);
  return loading;
}

/**
 * Насколько часто один и тот же звук может повториться.
 *
 * Десять сообщений подряд не должны выстроить десять окликов: в системном
 * оповещении это решает метка (см. `shared/lib/notify`), а здесь — время.
 * Значение подобрано по длине самого длинного указания: пока звучит одно,
 * второе того же рода не начинается.
 */
const GAP = 400;
const played = new Map<Cue, number>();

/**
 * Сыграть указание.
 *
 * Ничего не ждёт и ничем не отвечает: место вызова — обработчик события,
 * разбираться там, почему не вышло, негде и незачем. Тот же расчёт, что и у
 * `notify`.
 */
export function cue(name: Cue) {
  const volume = cueVolume();
  // В режиме стримера сигналы молчат — их слышно на трансляции.
  if (volume <= 0 || useStreamer.getState().on) return;
  // «Показ экрана» в настройках — одно событие: начало и конец гасятся вместе.
  if (useCuesOff.getState()[name === "share-stop" ? "share-start" : name]) return;

  const now = performance.now();
  if (now - (played.get(name) ?? -Infinity) < GAP) return;
  played.set(name, now);

  void load(name).then((buffer) => {
    if (!buffer) return;

    /*
     * Мимо общего узла вывода, прямо на выход.
     *
     * Через него звук попал бы под общую громкость и под полоску уровня в
     * настройках. Первое неверно по существу (см. `CUE_KEY`), а второе —
     * потому что полоска показывает, как слышно собеседников, и подскакивать
     * от собственного оклика ей незачем.
     */
    const context = audioContext();
    const gain = context.createGain();
    gain.gain.value = volume;
    gain.connect(context.destination);

    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(gain);
    // Узлы одноразовые: живут ровно одно звучание и снимаются сами.
    source.onended = () => gain.disconnect();
    source.start();
  });
}
